/**
 * Tabulated results, and the trace that explains them.
 *
 * The calculation trace is persisted as JSON with the engine version stamped on it, which is
 * what makes a historical result reproducible and diffable (D9). It is also what answers "why is
 * this dancer second" to someone standing at the table, expandable down to the criterion
 * (ux.md §3.8).
 *
 * Approval and publication are separate, deliberate, permissioned steps. A result that has been
 * published and then overridden stays visibly marked as overridden in every downstream view,
 * including the public leaderboard.
 */

import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { audited, id, instant, json, ref } from "./columns.js";

export const RESULT_SET_STATUSES = ["draft", "approved", "published", "superseded"] as const;

export const resultSets = sqliteTable(
  "result_sets",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    competitionId: ref("competition_id").notNull(),
    categoryId: ref("category_id"),
    phaseId: ref("phase_id"),
    sessionId: ref("session_id"),

    /** Both versions are stamped: the code that computed it and the configuration it read. */
    engineVersion: text("engine_version").notNull(),
    scoringConfigVersion: integer("scoring_config_version").notNull(),
    computedAt: instant("computed_at").notNull(),

    status: text("status", { enum: RESULT_SET_STATUSES }).notNull().default("draft"),
    approvedBy: ref("approved_by"),
    approvedAt: instant("approved_at"),
    publishedBy: ref("published_by"),
    publishedAt: instant("published_at"),
    supersededBy: ref("superseded_by"),
    ...audited(),
  },
  (table) => [
    index("result_sets_category_idx").on(table.categoryId, table.status),
    index("result_sets_event_idx").on(table.eventId, table.status),
  ],
);

export const results = sqliteTable(
  "results",
  {
    id: id(),
    resultSetId: ref("result_set_id").notNull(),
    communityId: ref("community_id").notNull(),
    registrationEntryId: ref("registration_entry_id").notNull(),
    rank: integer("rank").notNull(),
    total: integer("total").notNull(),

    /** Which tie-break rule decided this position, if one did. Surfaced explicitly with its
     *  inputs rather than left implicit (ux.md §3.8). */
    tieBreakApplied: text("tie_break_applied"),

    /** An override replaces the computed rank but never erases it. Both are shown. */
    overrideRank: integer("override_rank"),
    overrideReason: text("override_reason"),
    overrideBy: ref("override_by"),
    overrideAt: instant("override_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("results_set_entry_unique").on(table.resultSetId, table.registrationEntryId),
    index("results_set_rank_idx").on(table.resultSetId, table.rank),
  ],
);

export const calculationTraces = sqliteTable(
  "calculation_traces",
  {
    id: id(),
    resultSetId: ref("result_set_id").notNull(),
    communityId: ref("community_id").notNull(),
    engineVersion: text("engine_version").notNull(),
    traceJson: json("trace_json"),
    ...audited(),
  },
  (table) => [uniqueIndex("calculation_traces_set_unique").on(table.resultSetId)],
);

export const APPROVAL_DECISIONS = ["approved", "rejected"] as const;

export const approvals = sqliteTable(
  "approvals",
  {
    id: id(),
    resultSetId: ref("result_set_id").notNull(),
    communityId: ref("community_id").notNull(),
    approverUserId: ref("approver_user_id").notNull(),
    decision: text("decision", { enum: APPROVAL_DECISIONS }).notNull(),
    reason: text("reason"),
    ...audited(),
  },
  (table) => [index("approvals_set_idx").on(table.resultSetId)],
);
